import { ReactNode, useEffect } from 'react'

import ArrowLeftIcon from '@heroicons/react/outline/esm/ArrowLeftIcon.js'
import ChatAlt2Icon from '@heroicons/react/outline/esm/ChatAlt2Icon.js'
import KeyIcon from '@heroicons/react/outline/esm/KeyIcon.js'
import ViewGridIcon from '@heroicons/react/outline/esm/ViewGridIcon.js'
import { Link, matchPath, useLocation } from 'react-router-dom'

import { T } from '@tribeplatform/react-components/i18n'
import { useNetwork } from '@tribeplatform/react-sdk/hooks'
import { BellIcon } from '@tribeplatform/react-ui-kit/icons'
import { Container } from '@tribeplatform/react-ui-kit/Layout'
import { NavVertical } from '@tribeplatform/react-ui-kit/Sidebar'

import { useLogin } from '../../hooks/useLogin.js'
import { SlateLayout } from '../Layout/layout.enum.js'
import { PageLayout } from '../Layout/PageLayout.js'

export const SettingsLayout = ({ children }: { children: ReactNode }) => {
  const location = useLocation()

  const { data: network } = useNetwork()
  const { privateMessagingEnabled } = network?.settings?.privateMessaging || {}

  const { isLoggedIn, showLogin } = useLogin()

  useEffect(() => {
    if (!isLoggedIn) {
      showLogin()
    }
  }, [isLoggedIn, showLogin])

  const items = [
    {
      icon: <ArrowLeftIcon className="rtl:-scale-x-100" />,
      text: (
        <T defaultMessage="Back to {COMMUNITY}" id="Generics.BackToCommunity" />
      ),
      link: '/',
    },
    {
      icon: <KeyIcon />,
      text: <T defaultMessage="Account" id="Generics.Account" />,
      link: '/settings/account',
    },
    // { icon: <LockClosedIcon />, text: 'Security', link: '/settings/security' },
    {
      icon: <BellIcon />,
      text: <T defaultMessage="Notifications" id="Generics.Notifications" />,
      link: '/settings/notifications',
    },
    {
      text: (
        <T
          defaultMessage="Connected apps"
          id="Settings.ConnectedApps.ConnectedApps"
        />
      ),
      icon: <ViewGridIcon />,
      link: '/settings/connected-apps',
    },
  ]

  if (privateMessagingEnabled) {
    items.push({
      text: (
        <T defaultMessage="Private messaging" id="Generics.PrivateMessaging" />
      ),
      icon: <ChatAlt2Icon />,
      link: '/settings/messaging',
    })
  }

  return (
    <PageLayout layout={SlateLayout.CLASSIC}>
      <Container size="xl" direction="grid">
        <Container size="sm" padding={{ size: 'md', onlyOnMobile: true }}>
          <NavVertical>
            <NavVertical.Group>
              {items.map(item => {
                const active = matchPath(
                  {
                    path: item.link,
                    end: true,
                    caseSensitive: true,
                  },
                  location.pathname,
                )

                return (
                  <NavVertical.Item
                    key={item.link}
                    active={!!active}
                    as={Link}
                    leadingIcon={item.icon}
                    to={item.link}
                  >
                    {item.text}
                  </NavVertical.Item>
                )
              })}
            </NavVertical.Group>
          </NavVertical>
        </Container>
        <Container size="md">{children}</Container>
      </Container>
    </PageLayout>
  )
}
